import React from "react";
import { Popover, Box, Typography, Divider, Grid, TextField, Button } from "@mui/material";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

const MeetingDetailsPopover = ({
  open,
  anchorEl,
  onClose,
  selectedEvent,
  employeedLoggedId,
  comment,
  setComment,
  handleSaveComment,
}) => {
  if (!selectedEvent) return null;

  const isOwner = String(selectedEvent.resource.interviewerId) === String(employeedLoggedId);
  const { t } = useTranslation();

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      transformOrigin={{ vertical: "top", horizontal: "center" }}
      slotProps={{
        paper: {
          sx: {
            backgroundColor: "#f9f9f9",
            boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.25)",
            borderRadius: "12px",
            border: "1px solid #e0e0e0",
          },
        },
      }}
    >
      <Box sx={{ p: 2, maxWidth: 400 }}>
        <Typography variant="h5" component="div" gutterBottom>
          {t("admin.talentAcquisition.meetings.scheduledInterviews")}
        </Typography>
        <Divider sx={{ mb: 1 }} />

        <Grid container spacing={1}>
          <Grid item xs={12}>
            <Typography variant="body2">
              <strong>{t("admin.talentAcquisition.meetings.employee")}</strong>{" "}
              {isOwner
                ? t("admin.talentAcquisition.meetings.you")
                : selectedEvent.resource.interviewerName}
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="body2">
              <strong>{t("admin.talentAcquisition.meetings.applicant")}</strong>{" "}
              {selectedEvent.resource.applicantName}
            </Typography>
          </Grid>

          <Grid item xs={6}>
            <Typography variant="body2">
              <strong>{t("admin.talentAcquisition.meetings.rating")}:</strong>{" "}
              {selectedEvent.resource.rating}
            </Typography>
          </Grid>

          <Grid item xs={6}>
            <Typography variant="body2">
              <strong>{t("admin.talentAcquisition.meetings.status")}</strong>{" "}
              {t(`admin.talentAcquisition.statusInMeeting.${selectedEvent.resource.status}`)}
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="body2">
              <strong>{t("admin.talentAcquisition.meetings.email")}</strong>{" "}
              {selectedEvent.resource.email}
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="body2">
              <strong>{t("admin.talentAcquisition.meetings.phone")}</strong>{" "}
              {selectedEvent.resource.phone}
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ mb: 2, mt: 1 }} />
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: "bold" }}>
              {t("admin.talentAcquisition.meetings.interviewNotes")}
            </Typography>

            {isOwner ? (
              <TextField
                label={t("admin.talentAcquisition.meetings.addComment")}
                multiline
                rows={3}
                fullWidth
                variant="outlined"
                size="small"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
              />
            ) : (
              <Typography
                variant="body2"
                sx={{ whiteSpace: "pre-wrap", color: comment ? "inherit" : "text.secondary" }}
              >
                {comment || t("admin.talentAcquisition.meetings.noComment")}
              </Typography>
            )}
          </Grid>

          {isOwner && (
            <Grid item xs={12} display="flex" justifyContent="flex-end">
              <Button
                color="primary"
                sx={{
                  borderRadius: "8px",
                  mt: 1,
                  "&:hover": { backgroundColor: "blue", color: "#fff" },
                }}
                size="small"
                onClick={handleSaveComment}
              >
                {t("common.save")}
              </Button>
            </Grid>
          )}
        </Grid>
      </Box>
    </Popover>
  );
};

MeetingDetailsPopover.propTypes = {
  open: PropTypes.bool.isRequired,
  anchorEl: PropTypes.oneOfType([
    PropTypes.instanceOf(Element),
    PropTypes.object, // For the virtual anchor logic
  ]),
  onClose: PropTypes.func.isRequired,
  selectedEvent: PropTypes.shape({
    resource: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      interviewerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      interviewerName: PropTypes.string,
      applicantName: PropTypes.string,
      rating: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      status: PropTypes.string,
      email: PropTypes.string,
      phone: PropTypes.string,
    }),
  }),
  employeedLoggedId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  comment: PropTypes.string,
  setComment: PropTypes.func.isRequired,
  handleSaveComment: PropTypes.func.isRequired,
};

export default MeetingDetailsPopover;
